/**
 * AnalyticsPage
 * Analytics dashboard with energy/revenue charts and top locations
 */

import { useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAnalyticsStore } from '../../stores/analytics-store';
import { DateRangeSelector } from '../molecules/DateRangeSelector';
import { AnalyticsSummaryRow } from '../molecules/AnalyticsSummaryRow';
import { EnergyConsumptionChart } from '../organisms/EnergyConsumptionChart';
import { RevenueChart } from '../organisms/RevenueChart';
import { TopLocationsTable } from '../organisms/TopLocationsTable';
import { PageSkeleton } from '../atoms/SkeletonCard';

export function AnalyticsPage() {
  const { selectedOperator } = useOperatorStore();

  // Use selectors to prevent unnecessary re-renders of charts when only top locations change
  const energyData = useAnalyticsStore((state) => state.energyData);
  const revenueData = useAnalyticsStore((state) => state.revenueData);
  const summary = useAnalyticsStore((state) => state.summary);
  const topLocations = useAnalyticsStore((state) => state.topLocations);
  const dateRange = useAnalyticsStore((state) => state.dateRange);
  const selectedPreset = useAnalyticsStore((state) => state.selectedPreset);
  const isLoading = useAnalyticsStore((state) => state.isLoading);
  const isLoadingTopLocations = useAnalyticsStore((state) => state.isLoadingTopLocations);
  const error = useAnalyticsStore((state) => state.error);
  const lastUpdated = useAnalyticsStore((state) => state.lastUpdated);
  const topLocationsLimit = useAnalyticsStore((state) => state.topLocationsLimit);
  const topLocationsSortBy = useAnalyticsStore((state) => state.topLocationsSortBy);
  const loadAnalytics = useAnalyticsStore((state) => state.loadAnalytics);
  const setPreset = useAnalyticsStore((state) => state.setPreset);
  const setDateRange = useAnalyticsStore((state) => state.setDateRange);
  const refresh = useAnalyticsStore((state) => state.refresh);

  useEffect(() => {
    if (selectedOperator?.operatorId) {
      loadAnalytics();
    }
  }, [selectedOperator?.operatorId, loadAnalytics]);

  // Handle top locations sort/limit change
  const handleSortChange = useCallback((sortBy: 'revenue' | 'energy' | 'sessions', limit: number) => {
    useAnalyticsStore.getState().loadTopLocations(sortBy, limit);
  }, []);

  // Format last updated time
  const formatLastUpdated = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Show skeleton on initial load
  if (isLoading && !summary) {
    return <PageSkeleton />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-end px-6 py-2 bg-white">
        <div className="flex items-center gap-4">
          <DateRangeSelector
            selectedPreset={selectedPreset}
            dateRange={dateRange}
            onPresetChange={setPreset}
            onDateRangeChange={setDateRange}
            isLoading={isLoading}
          />
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {lastUpdated && (
              <span>Updated {formatLastUpdated(lastUpdated)}</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={refresh}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 pb-8 space-y-6 bg-white">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <p className="font-medium">Error loading analytics</p>
            <p className="text-sm mt-1">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Component 1: Revenue and Sessions */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue and Sessions</h2>

          {/* Summary Stats */}
          <AnalyticsSummaryRow summary={summary} isLoading={isLoading} />

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <RevenueChart data={revenueData} isLoading={isLoading} />
            <EnergyConsumptionChart data={energyData} isLoading={isLoading} />
          </div>
        </div>

        {/* Component 2: Top Locations */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Locations</h2>
          <TopLocationsTable
            locations={topLocations}
            isLoading={isLoadingTopLocations}
            onSortChange={handleSortChange}
            defaultLimit={topLocationsLimit}
            defaultSortBy={topLocationsSortBy}
          />
        </div>
      </div>
    </div>
  );
}
